import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import authService from './services/auth.service';
import Layout from './components/layout/Layout';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { StockDataProvider } from './context/StockDataContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './context/ThemeContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import './index.css';


// Create MUI theme function that supports dark mode
const createAppTheme = (darkMode: boolean) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      light: '#3389db',
      main: '#1173d4',
      dark: '#0d5ba8',
    },
    secondary: {
      light: '#9CA3AF',
      main: '#6B7280',
      dark: '#4B5563',
    },
    background: {
      default: darkMode ? '#101922' : '#f6f7f8',
      paper: darkMode ? '#1a2635' : '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Lazy load pages for better performance
const HomePage = React.lazy(() => import('./pages/Home'));
const MarketsPage = React.lazy(() => import('./pages/Markets'));
const SearchPage = React.lazy(() => import('./pages/Search'));
const WatchlistPage = React.lazy(() => import('./pages/Watchlist'));
const ProfilePage = React.lazy(() => import('./pages/Profile'));
const LoginPage = React.lazy(() => import('./pages/auth/Login'));
const SignupPage = React.lazy(() => import('./pages/auth/Signup'));
const EnhancedPredictionPage = React.lazy(() => import('./pages/EnhancedPrediction'));
const TrainingPage = React.lazy(() => import('./pages/Training'));
const PullbacksPage = React.lazy(() => import('./pages/PullbacksPage'));
const StockDetailsPage = React.lazy(() => import('./pages/StockDetails'));
const AlertsPage = React.lazy(() => import('./pages/AlertsPage'));
const RecordedTradesPage = React.lazy(() => import('./pages/RecordedTradesPage'));
const AdminBlueChipPage = React.lazy(() => import('./pages/AdminBlueChipPage'));
const ProScannerPage = React.lazy(() => import('./pages/ProScannerPage'));
const DayTradingDashboard = React.lazy(() => import('./pages/DayTradingDashboard'));
const ScalperDashboard = React.lazy(() => import('./pages/ScalperDashboard'));
const WhipsawDashboard = React.lazy(() => import('./pages/WhipsawDashboard'));
const AmIAScalperDashboard = React.lazy(() => import('./pages/AmIAScalperDashboard'));
const MarketScannerPage = React.lazy(() => import('./pages/MarketScanner'));
const SwingTraderDashboard = React.lazy(() => import('./pages/SwingTraderDashboard'));
const MomentumTraderDashboard = React.lazy(() => import('./pages/MomentumTraderDashboard'));
const CommunityFeedPage = React.lazy(() => import('./pages/CommunityFeedPage'));
const JobScoutPage = React.lazy(() => import('./pages/JobScoutPage'));

const AuthInterceptorSetup = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    const handleSessionExpired = () => {
      showToast('Your session has expired. Please log in again.', 'error');
      navigate('/login');
    };

    authService.setupAxiosInterceptors(handleSessionExpired);
  }, [navigate, showToast]);

  return null; // This component does not render anything
};

const AppContent = () => {
  const { darkMode } = useTheme();
  const theme = React.useMemo(() => createAppTheme(darkMode), [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthInterceptorSetup />
      <Routes>
                {/* Auth routes */}
                <Route path="/login" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <LoginPage />
                </React.Suspense>
              } />
              <Route path="/signup" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <SignupPage />
                </React.Suspense>
              } />

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <HomePage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/markets" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <MarketsPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/search" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <SearchPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/watchlist" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <WatchlistPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <ProfilePage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/enhanced-prediction" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <EnhancedPredictionPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/training" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <TrainingPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/pullbacks" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <PullbacksPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/market-scanner" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <MarketScannerPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/stocks/:symbol" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <StockDetailsPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/alerts" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <AlertsPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/recorded-trades" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <RecordedTradesPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/community" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <CommunityFeedPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/pro-scanner" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <ProScannerPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/swing-trader" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <SwingTraderDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/momentum-trader" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <MomentumTraderDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/day-trading" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <DayTradingDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/scalper" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <ScalperDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/whipsaw-trader" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <WhipsawDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/am-i-a-scalper" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <AmIAScalperDashboard />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/job-scout" element={
                <ProtectedRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <JobScoutPage />
                    </React.Suspense>
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin/blue-chip" element={
                <AdminRoute>
                  <Layout>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <AdminBlueChipPage />
                    </React.Suspense>
                  </Layout>
                </AdminRoute>
              } />
              
              <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <StockDataProvider>
              <CustomThemeProvider>
                <AppContent />
              </CustomThemeProvider>
            </StockDataProvider>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
